import '../../modules/mode-components';

import * as angular from 'angular';
import { BehaviorSubject, Observable } from 'rxjs';

import { FeatureFlag, FeatureFlagsFacade, Logger } from '@mode/shared/contract-common';
import { getAppName, getCurrentUsername, getOwnerUsername, getThoughtspotIntegrated } from '@mode/shared/util-js';
import { AppStore } from '~/types/main.types';
import { map } from 'rxjs/operators';

class GroupShowController {
  static $inject = [
    '$scope',
    '$state',
    'AccountModel',
    '$log',
    'GroupModel',
    'GroupMembershipModel',
    '$q',
    'ModeCollection',
    'ToastService',
    'SearchClientService',
    '$timeout',
    '$window',
    'RxStoreService',
    'FeatureFlagsFacade',
  ];

  readonly appName = getAppName();
  public group: any;
  public group$ = new BehaviorSubject<any>(null);

  public groupToken: any;
  public owner: any;
  public ownerToken: any;
  public account: any;
  public groupAccount: string;
  private groupError: string;
  private memberships: any[];
  private searchMemberships: any[];
  private membershipsCollection: any;
  private query: string;
  private membershipsLoaded: boolean;
  private searchMembershipsLoaded: boolean;
  private selectedTab: 'members' | 'spaces' | 'connections';
  private groupsVisibility$: Observable<boolean>;
  private addMembersButtonEnabled$: Observable<boolean>;
  private settingsMenuEnabled$: Observable<boolean>;
  private thoughtspotIntegrated: boolean;

  constructor(
    private $scope,
    private $state,
    private AccountModel,
    private $log: Logger,
    private GroupModel,
    private GroupMembershipModel,
    private $q,
    private ModeCollection,
    private AlertService,
    private _searchClientService,
    private $timeout: angular.ITimeoutService,
    private $window: angular.IWindowService,
    private rxStore: AppStore,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.groupsVisibility$ = this.featureFlagsFacade.asObservable(FeatureFlag.GroupsVisibility);
    this.thoughtspotIntegrated = getThoughtspotIntegrated(this.$window.document);
    this.addMembersButtonEnabled$ = this.group$.pipe(
      map(
        (group) =>
          this.isMembersTab() &&
          group &&
          group._resource.hasEmbedded('new_group_membership') &&
          !this.groupIsManaged(group) &&
          !this.thoughtspotIntegrated
      )
    );
    this.settingsMenuEnabled$ = this.group$.pipe(
      map((group) => this.getEditPrivilege(group) && !this.thoughtspotIntegrated)
    );
  }

  $onInit() {
    this.initEmptyMemberships();
    this.membershipsLoaded = false;
    this.getOwnerAndAccount()
      .then(this.getGroup.bind(this))
      .then(this.getGroupMemberships.bind(this))
      .then(this.watchMemberships.bind(this))
      .then(this.buildMemberships.bind(this))
      .catch((e) => {
        this.groupError = e;
        this.$log.warn('Error getting groups', e);
        this.finishMemberships();
      });
    if (this.isMembersTab()) {
      this.selectedTab = 'members';
    } else if (this.isSpacesTab()) {
      this.selectedTab = 'spaces';
    } else if (this.isConnectionsTab()) {
      this.selectedTab = 'connections';
    }
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.group && changes.group.currentValue) {
      this.group$.next(changes.group.currentValue);
    }
  }

  isMembersTab() {
    if (this.$state.includes('org-settings.group.members')) {
      return true;
    }
    return false;
  }

  isSpacesTab() {
    if (this.$state.includes('org-settings.group.spaces')) {
      return true;
    }
    return false;
  }

  isConnectionsTab() {
    if (this.$state.includes('org-settings.group.connections')) {
      return true;
    }
    return false;
  }

  groupIsManaged(group = this.group): boolean {
    // determine if a group is managed by SCIM
    return !!(group && group._resource.managed);
  }

  settingsTooltipHtml() {
    return `This Group is managed through SCIM and cannot be modified in ${this.appName}`;
  }

  initEmptyMemberships() {
    this.memberships = [];
    this.clearSearchMemberships();
  }

  finishMemberships() {
    this.membershipsLoaded = true;
  }

  getOwnerAndAccount() {
    const ownerUsername = this.groupAccount ? this.groupAccount : getOwnerUsername(this.$window.document);
    const accountUsername = getCurrentUsername(this.$window.document);
    return this.$q
      .all([
        this.AccountModel.find({
          username: ownerUsername,
          useEntryPath: true,
          refresh: true,
          includes: ['new_group'],
        }),
        this.AccountModel.find({
          username: accountUsername,
          useEntryPath: true,
        }),
      ])
      .then(this.$q.spread(this.saveOwnerAndAccount.bind(this)));
  }

  saveOwnerAndAccount(owner, account) {
    this.owner = owner;
    this.ownerToken = owner.token;
    this.account = account;
    return owner;
  }

  getGroup(owner) {
    if (!this.group && this.groupToken) {
      return this.GroupModel.find({
        account: this.owner.username,
        token: this.groupToken,
        includes: 'new_group_membership',
      });
    } else {
      return this.group;
    }
  }

  getGroupMemberships(group) {
    this.group = group;
    this.group$.next(this.group);
    return group.groupMemberships({ includes: { group_memberships: 'member' } });
  }

  watchMemberships(membershipsCollection) {
    this.membershipsCollection = membershipsCollection;
    membershipsCollection.watch(this.$scope, this.buildMemberships.bind(this));
    return membershipsCollection.elements();
  }

  buildMemberships(memberships) {
    return this.GroupMembershipModel.buildMembershipsWithAccount(memberships).then((memberships) => {
      this.memberships = memberships;
      this.finishMemberships();
    });
  }

  removeMembership(membership) {
    membership
      .destroy()
      .then(() => {
        this.memberships = this.memberships.filter((m) => m.member_token !== membership.member_token);
        this.searchMemberships = this.searchMemberships.filter((m) => m.token !== membership.member_token);
        this.AlertService.success('Removed user');
      })
      .catch(() => {
        this.AlertService.error('Could not remove user');
      });
  }

  removeSearchMembership(searchMembership) {
    this.group.findMembership(searchMembership.objectID).then((membership) => {
      if (membership) {
        this.removeMembership(membership);
      } else {
        this.searchMemberships = this.searchMemberships.filter((m) => m.token !== searchMembership.token);
        this.AlertService.success('Removed user');
      }
    });
  }

  destroyGroup() {
    this.group
      .destroy()
      .then(() => (window.location.href = this.owner.groupsIndexLink))
      .catch(() => this.AlertService.error('Could not delete group'));
  }

  handleQueryChanged() {
    if (!this.query) {
      this.clearSearchMemberships();
      return;
    }
    this.searchMembershipsLoaded = false;

    const opts = {
      query: this.query,
      indexes: ['user'],
      perPage: 20,
      clearCache: true,
      filters: `organization_${this.owner.token},group_${this.group.token}`,
    };

    this._searchClientService
      .query(opts)
      .then(this.handleSearchSuccess.bind(this))
      .catch(this.handleSearchError.bind(this));
  }

  handleSearchSuccess(response) {
    this.searchMemberships = response.user || [];
    this.searchMembershipsLoaded = true;
  }

  handleSearchError() {
    this.clearSearchMemberships();
    this.finishSearchMemberships();
  }

  clearSearchMemberships() {
    this.searchMemberships = [];
  }

  finishSearchMemberships() {
    this.searchMembershipsLoaded = true;
  }

  getEditPrivilege(group = this.group) {
    return (group && group._resource.hasForm('edit') && !this.groupIsManaged()) || false;
  }
}

export const groupShowComponent: angular.IComponentOptions = {
  bindings: {
    group: '<?',
    groupToken: '<?',
    groupAccount: '<?',
    owner: '<?',
    account: '<?',
  },
  template: `
<div class="settings-action-hd clearfix group-show-breadcrumbs">
  <h2 class="main-hd">
    <span class="settings-breadcrumb-parent"><a ng-href="{{ ctrl.owner.groupsIndexLink }}" target="_self">Groups</a> > </span>
    <span class="main-hd-truncate">{{ ctrl.group.name }}</span>
    <span
      class="scim-badge"
      ng-if="ctrl.groupIsManaged()"
      uib-tooltip="{{ ctrl.settingsTooltipHtml() }}"
      tooltip-append-to-body="true"
      tooltip-placement="bottom"
      tooltip-class="wrapping-tooltip-lg"
      tooltip-popup-delay="500"
    >
      SCIM
    </span>
    <span
      uib-dropdown
      ng-if="ctrl.settingsMenuEnabled$ | async:this"
      class="dropdown settings-header-dropdown"
      ng-disabled="ctrl.groupIsManaged()">
      <span uib-dropdown-toggle class="dropdown-toggle">
        <capra-icon icon="icon-gear" ng-if="!ctrl.groupIsManaged()"></capra-icon>
      </span>
      <ul uib-dropdown-menu class="dropdown-menu group-show-dropdown">
        <li>
          <button
            rename-group
            group="ctrl.group"
            owner="ctrl.owner">
            Rename
          </button>
        </li>
        <li>
          <button
             confirm-modal="Are you sure you want to delete this group?"
             confirm-modal-title="Delete Group?"
             confirm-modal-button-confirm="Delete"
             confirm-modal-button-class="btn-danger"
             confirm-modal-on-confirm="ctrl.destroyGroup()">
            Delete...
          </button>
        </li>
      </ul>
    </span>
  </h2>
  <capra-button
    ng-if="ctrl.addMembersButtonEnabled$ | async:this"
    class="action-button"
    group-add-membership-modal
    group="ctrl.group"
    owner="ctrl.owner"
    account="ctrl.account"
    data-test="add-members"
    text="Add Members"
    size="small"
  ></capra-button>
</div>

<div ng-if="(!ctrl.groupsVisibility$ | async:this)" class="well">
  <div class="group-member-filter">
    <input
      type="text"
      ng-if="ctrl.memberships.length !== 0 && ctrl.membershipsLoaded"
      class="form-control form-control-block next-form-control icon-filter-icon form-control-filter"
      autocomplete="off"
      placeholder="Find members..."
      ng-change="ctrl.handleQueryChanged()"
      ng-model-options="{ debounce: 100 }"
      ng-model="ctrl.query" />
  </div>
  <div class="settings-loading" ng-if="!ctrl.membershipsLoaded">
    <mode-loader></mode-loader>
  </div>
  <div class="empty-msg" ng-if="!ctrl.memberships.length && ctrl.membershipsLoaded">
    <h3 class="group-member-empty-header">This group is about ready to roll</h3>
    <p class="group-member-empty-message">Add members to organize people into familiar teams or workgroups.</p>
  </div>
  <div class="empty-msg" ng-if="!ctrl.searchMemberships.length && ctrl.searchMembershipsLoaded && ctrl.query">
    <h3 class="group-member-empty-header">Hmmm...looks like there are no matching members</h3>
    <p class="group-member-empty-message">Try using a different filter.</p>
  </div>
  <ul class="group-member-list">
    <li ng-repeat="membership in ctrl.memberships" class="group-member-list-item" ng-if="!ctrl.query">
      <mode-avatar options="membership.accountMember.avatar" size="35" class="member-avatar"></mode-avatar>
      <div class="member-details">
        <div class="primary-content">{{ membership.accountMember.displayName() }}</div>
      </div>
      <div class="member-action" ng-if="membership._resource.hasForm('destroy')">
        <button class="btn btn-lg btn-danger" ng-click="ctrl.removeMembership(membership)">Remove</button>
      </div>
    </li>
    <li ng-repeat="membership in ctrl.searchMemberships" class="group-member-list-item" ng-if="ctrl.query">
      <mode-avatar options="membership.avatar" size="35" class="member-avatar"></mode-avatar>
      <div class="member-details">
        <div class="primary-content">{{ membership.name }}</div>
      </div>
      <div class="member-action" ng-if="ctrl.group._resource.hasEmbedded('new_group_membership')">
        <button class="btn btn-lg btn-danger" ng-click="ctrl.removeSearchMembership(membership)">Remove</button>
      </div>
    </li>
  </ul>
</div>

<div ng-if="ctrl.memberships">
  <ul ng-if="(ctrl.groupsVisibility$ | async:this)" class="nav nav-tabs nav-tabs-bottom settings-tabs">
    <li ng-class="{active:ctrl.isMembersTab()}">
      <a ui-sref="org-settings.group.members">Members</a>
    </li>
    <li ng-class="{active:ctrl.isSpacesTab()}">
      <a ui-sref="org-settings.group.spaces">Collections</a>
    </li>
    <li ng-class="{active:ctrl.isConnectionsTab()}">
      <a  ui-sref="org-settings.group.connections">Connections</a>
    </li>
  </ul>
  <div ng-if="ctrl.$state.includes('org-settings.group.members')">
    <group-members-browser
      org-token="ctrl.ownerToken"
      group-token="ctrl.groupToken"
      group-memberships="ctrl.memberships"
      can-edit-group="ctrl.getEditPrivilege()"
      dropdown-tooltip-enabled="ctrl.groupIsManaged()"
    />
  </div>
  <div ui-view></div>
</div>
`,
  controller: GroupShowController,
  controllerAs: 'ctrl',
};

angular.module('mode.components').component('groupShow', groupShowComponent);
